// TODO: Consider extracting Combobox's inner logic (cmdk Command wrapper) in Popcorn
// so it can be reused in contexts outside of Popover (e.g., inside DropdownMenu, Drawer, Sheet).
// Currently we use cmdk directly here because Combobox is tightly coupled to Popover.

import { Command } from 'cmdk'

import { clsx } from 'clsx'

import {
  AllChannelsIcon,
  ChannelAvatar,
  Checkbox,
  DropdownMenu,
  Flex,
  Input,
  PlusIcon,
  SearchIcon,
  Text,
  Tooltip,
  UnstyledButton,
} from '@buffer-mono/popcorn'

import {
  ChannelSelector,
  useChannelGroupsFilter,
} from '~publish/components/ChannelSelector'
import type { Service } from '~publish/pages/Community/graphqlEnums'

import styles from './ChannelFilterSubMenu.module.css'

import type { JSX } from 'react'

export type ChannelFilterSubMenuChannel = {
  id: string
  name: string
  service: Service
  avatar: string
}

export type ChannelFilterSubMenuProps = {
  channels: readonly ChannelFilterSubMenuChannel[]
  selectedChannelIds: string[]
  onChannelSelect: (channelIds: string[]) => void
  /**
   * Channels that can't be filtered on here — rendered greyed out and
   * unselectable, and left out of "Select all".
   */
  disabledChannelIds?: Set<string>
  /**
   * Per-channel explanation shown in a tooltip on a disabled row. Only called
   * for ids in {@link disabledChannelIds}; returning nothing leaves the row
   * without a tooltip.
   */
  getDisabledChannelReason?: (channelId: string) => React.ReactNode
}

export function ChannelFilterSubMenu({
  channels,
  selectedChannelIds,
  onChannelSelect,
  disabledChannelIds,
  getDisabledChannelReason,
}: ChannelFilterSubMenuProps): JSX.Element {
  // Unified channel groups filter logic. Groups are a second route to the same
  // selection, so they take the disabled set too — otherwise picking a group
  // would select the very channels the rows below refuse to.
  const {
    filteredChannelGroups,
    selectedChannelGroupIds,
    disabledChannelGroupIds,
    handleSelectGroup,
    isExpanded: isChannelGroupsExpanded,
    setIsExpanded: setIsChannelGroupsExpanded,
  } = useChannelGroupsFilter({
    selectedChannelIds,
    onChannelSelect,
    disabledChannelIds,
  })

  const toggleChannel = (channelId: string): void => {
    const newSelection = selectedChannelIds.includes(channelId)
      ? selectedChannelIds.filter((id) => id !== channelId)
      : [...selectedChannelIds, channelId]
    onChannelSelect(newSelection)
  }

  const handleSelectAll = (): void => {
    if (selectedChannelIds.length > 0) {
      onChannelSelect([])
    } else {
      onChannelSelect(
        channels
          .filter((channel) => !disabledChannelIds?.has(channel.id))
          .map((channel) => channel.id),
      )
    }
  }

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger>
        <AllChannelsIcon /> Filter by channel
      </DropdownMenu.SubTrigger>
      <DropdownMenu.SubMenu>
        <Command loop className={styles.command}>
          {channels.length > 0 && (
            <Command.Input asChild>
              <Input
                prefix={<SearchIcon />}
                placeholder="Search channels..."
                className={styles.searchInput}
              />
            </Command.Input>
          )}
          {/* Guard the list, not the whole command, so the search input stays
              translatable. The list holds cmdk's churning channel rows plus the
              dynamic "Select all"/"Unselect all" toggle — both crash-prone under
              translation — so guarding here keeps them safe. See FilterByTag. */}
          <Command.List className={styles.list} translate="no">
            {filteredChannelGroups.length > 0 && (
              <ChannelSelector.GroupsOverflow
                groups={filteredChannelGroups}
                selectedIds={selectedChannelGroupIds}
                disabledIds={disabledChannelGroupIds}
                onSelectGroup={handleSelectGroup}
                isExpanded={isChannelGroupsExpanded}
                onExpand={() => setIsChannelGroupsExpanded(true)}
              >
                <ChannelSelector.NewGroupLink>
                  <PlusIcon size={16} />
                  Group
                </ChannelSelector.NewGroupLink>
              </ChannelSelector.GroupsOverflow>
            )}
            {channels.length > 0 && (
              <Flex
                justify="between"
                align="center"
                className={styles.selectAllHeader}
              >
                <Text>Channels</Text>
                <UnstyledButton onClick={handleSelectAll}>
                  <Text>
                    {selectedChannelIds.length > 0
                      ? 'Unselect all'
                      : 'Select all'}
                  </Text>
                </UnstyledButton>
              </Flex>
            )}
            <Command.Empty className={styles.empty}>
              {channels.length === 0
                ? 'No channels available'
                : 'No channels found'}
            </Command.Empty>
            {channels.map((channel) => {
              const isDisabled = disabledChannelIds?.has(channel.id) ?? false
              return (
                <ChannelItem
                  key={channel.id}
                  channel={channel}
                  isSelected={selectedChannelIds.includes(channel.id)}
                  isDisabled={isDisabled}
                  disabledReason={
                    isDisabled
                      ? getDisabledChannelReason?.(channel.id)
                      : undefined
                  }
                  onToggle={toggleChannel}
                />
              )
            })}
          </Command.List>
        </Command>
      </DropdownMenu.SubMenu>
    </DropdownMenu.Sub>
  )
}

type ChannelItemProps = {
  channel: ChannelFilterSubMenuChannel
  isSelected: boolean
  isDisabled: boolean
  disabledReason?: React.ReactNode
  onToggle: (channelId: string) => void
}

function ChannelItem({
  channel,
  isSelected,
  isDisabled,
  disabledReason,
  onToggle,
}: ChannelItemProps): JSX.Element {
  const row = (
    <>
      <Checkbox checked={isSelected} disabled={isDisabled} tabIndex={-1} />
      <ChannelAvatar
        size="small"
        alt=""
        src={channel.avatar}
        name={channel.name}
        channel={channel.service}
        className={clsx(isDisabled && styles.avatarDisabled)}
      />
      <span className={styles.channelName}>{channel.name}</span>
    </>
  )

  return (
    <Command.Item
      value={`${channel.name} ${channel.service}`}
      disabled={isDisabled}
      onSelect={isDisabled ? undefined : (): void => onToggle(channel.id)}
      className={clsx(styles.item, isDisabled && styles.itemDisabled)}
    >
      {/* The tooltip wraps the row content rather than the item: the item owns
          the pointer handlers cmdk needs, which would shadow the trigger's. */}
      {isDisabled && disabledReason ? (
        <Tooltip content={disabledReason}>
          <span className={styles.tooltipTarget}>{row}</span>
        </Tooltip>
      ) : (
        row
      )}
    </Command.Item>
  )
}
